import React, { useEffect, useState } from "react";
import { Routes, Route, useNavigate, useParams } from "react-router-dom";
import LeadDetailView from "../components/Leads/LeadDetailView";
import BulkUploadModal from "../components/Leads/BulkUploadModal";
import AssignLeadsModal from "../components/Leads/AssignLeadsModal";
import PageIcon from "../components/Layout/PageIcon";
import {
  Table,
  Button,
  Input,
  InputNumber,
  Select,
  Space,
  Tag,
  Typography,
  Card,
  Modal,
  Form,
  message,
  DatePicker,
  Row,
  Col,
  Badge,
  Upload,
  Avatar,
} from "antd";
import {
  PlusOutlined,
  FilterOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  ArrowLeftOutlined,
  ImportOutlined,
} from "@ant-design/icons";
import useAuthStore from "../store/authStore";
import useLeadStore from "../store/leadStore";
import useSettingsStore from "../store/settingsStore";
import apiService from "../services/apiService"; // Import apiService
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const LeadsPage = () => {
  return (
    <Routes>
      <Route index element={<LeadsList />} />
      <Route path="new" element={<LeadForm />} />
      <Route path=":id" element={<LeadDetailView />} />
      <Route path=":id/edit" element={<LeadForm />} />
    </Routes>
  );
};

const LeadsList = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const {
    leads,
    isLoading,
    pagination,
    filters,
    fetchLeads,
    deleteLead,
    setFilters,
    clearFilters,
  } = useLeadStore();
  const { formatCurrency, currencySymbol } = useSettingsStore();

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [leadToDelete, setLeadToDelete] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingTeamMembers, setLoadingTeamMembers] = useState(false);
  const [bulkUploadModalVisible, setBulkUploadModalVisible] = useState(false);
  const [assignModalVisible, setAssignModalVisible] = useState(false);

  // Derived filter values for Ant Design Select components to ensure placeholders are visible
  const statusFilterValue = filters.status === "" ? undefined : filters.status;
  const priorityFilterValue = filters.priority === "" ? undefined : filters.priority;
  const sourceFilterValue = filters.source === "" ? undefined : filters.source;
  const assignedToFilterValue = filters.assignedTo === "" ? undefined : filters.assignedTo;

  useEffect(() => {
    // Fetch leads using the current pagination and filters from the store
    fetchLeads({
      page: pagination.page,
      limit: pagination.limit,
      ...filters,
    });
    loadTeamMembers(); // Load team members when component mounts
  }, [fetchLeads, pagination.page, pagination.limit, filters]); // Add pagination and filters to dependency array

  const loadTeamMembers = async () => {
    setLoadingTeamMembers(true);
    try {
      const response = await apiService.get("/users/team-members");
      if (response.data.success) {
        setTeamMembers(response.data.data.teamMembers);
      }
    } catch (error) {
      console.error("Failed to load team members:", error);
    } finally {
      setLoadingTeamMembers(false);
    }
  };

  const handleSearch = (value) => {
    setFilters({ search: value });
    fetchLeads({ search: value, page: 1 });
  };

  const handleFilterChange = (key, value) => {
    // Ensure that if value is null or empty string, it's treated as undefined
    // so Ant Design Select displays the placeholder correctly.
    const filterValue = value === "" || value === null ? undefined : value;
    const newFilters = { ...filters, [key]: filterValue };
    setFilters(newFilters);
    fetchLeads({ ...newFilters, page: 1 });
  };

  const handleClearFilters = () => {
    // Explicitly set filter values to undefined to ensure placeholders are visible
    setFilters({
      search: undefined,
      status: undefined,
      priority: undefined,
      source: undefined,
      assignedTo: undefined, // Clear the assignedTo filter
    });
    // Then call clearFilters from the store, which should ideally do the same
    clearFilters();
    fetchLeads({
      page: 1,
      search: undefined,
      status: undefined,
      priority: undefined,
      source: undefined,
      assignedTo: undefined
    });
  };

  const handleTableChange = (pagination, filters, sorter) => {
    const params = {
      page: pagination.current,
      limit: pagination.pageSize,
      ...filters,
    };

    if (sorter.field) {
      params.sort = sorter.field;
      params.order = sorter.order === "ascend" ? "asc" : "desc";
    }

    fetchLeads(params);
  };

  const handleRowClick = (lead) => {
    navigate(`/leads/${lead._id}`);
  };

  const handleDelete = async (leadId) => {
    const result = await deleteLead(leadId);
    if (result.success) {
      message.success("Lead deleted successfully");
      setDeleteModalVisible(false);
      setLeadToDelete(null);
    } else {
      message.error(result.error || "Failed to delete lead");
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: "blue",
      contacted: "orange",
      converted: "success",
      lost: "error",
    };
    return colors[status] || "default";
  };

  const priorityMeta = {
    low: { label: "Cold", emoji: "❄️", tagColor: "red", rowBg: "#ffe5e5" },
    medium: { label: "Warm", emoji: "🌤️", tagColor: "orange", rowBg: "#fff4e0" },
    high: { label: "Hot", emoji: "🔥", tagColor: "#237804", rowBg: "#d9f7be" },
    urgent: { label: "Superhot", emoji: "🔥🔥", tagColor: "#135200", rowBg: "#c8f7c5" },
  };

  const getPriorityColor = (priority) => {
    return priorityMeta[priority]?.tagColor || "default";
  };

  const getPriorityLabel = (priority) => {
    return priorityMeta[priority]?.label || priority;
  };

  const getPriorityEmoji = (priority) => {
    return priorityMeta[priority]?.emoji || "";
  };

  const getPriorityRowColor = (priority) => {
    return priorityMeta[priority]?.rowBg || "transparent";
  };

  const countActiveFilters = () => {
    let count = 0;
    if (filters.search) count++;
    if (filters.status) count++;
    if (filters.priority) count++;
    if (filters.source) count++;
    if (filters.assignedTo) count++;
    return count;
  };

  const FilterTags = () => (
    <div className="flex flex-wrap gap-2 mt-4">
      {filters.search && (
        <Tag closable onClose={() => handleFilterChange('search', undefined)}>
          Search: {filters.search}
        </Tag>
      )}
      {filters.status && (
        <Tag closable onClose={() => handleFilterChange('status', undefined)}>
          Status: {filters.status}
        </Tag>
      )}
      {filters.priority && (
        <Tag closable onClose={() => handleFilterChange('priority', undefined)}>
          Priority: {getPriorityEmoji(filters.priority)} {getPriorityLabel(filters.priority)}
        </Tag>
      )}
      {filters.source && (
        <Tag closable onClose={() => handleFilterChange('source', undefined)}>
          Source: {filters.source}
        </Tag>
      )}
      {filters.assignedTo && (
        <Tag closable onClose={() => handleFilterChange('assignedTo', undefined)}>
          Assigned To: {teamMembers.find(m => m.id === filters.assignedTo)?.name || filters.assignedTo}
        </Tag>
      )}
    </div>
  );

  const columns = [
    {
      title: "Name",
      dataIndex: "firstName",
      key: "name",
      sorter: true,
      render: (text, record) => (
        <div className="flex items-center">
          <Avatar
            size={36}
            src={record.profileImageUrl}
            icon={record.profileImageUrl ? undefined : <UserOutlined />}
            className="mr-3"
            style={
              record.profileImageUrl
                ? {}
                : { backgroundColor: "#e0f2fe", color: "#0ea5e9" }
            }
          />
          <div>
            <div className="font-medium">
              {record.firstName} {record.lastName}
            </div>
            <div className="text-sm text-gray-500">{record.email}</div>
          </div>
        </div>
      ),
    },
    {
      title: "Company",
      dataIndex: "company",
      key: "company",
      sorter: true,
      render: (text, record) => (
        <div>
          <div>{record.company || "-"}</div>
          <div className="text-sm text-gray-500">{record.jobTitle || "-"}</div>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: [
        { text: "New", value: "new" },
        { text: "Contacted", value: "contacted" },
        { text: "Converted", value: "converted" },
        { text: "Lost", value: "lost" },
      ],
      render: (status) => (
        <Tag color={getStatusColor(status)}>{status?.toUpperCase()}</Tag>
      ),
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      filters: [
        { text: "❄️ Cold", value: "low" },
        { text: "🌤️ Warm", value: "medium" },
        { text: "🔥 Hot", value: "high" },
        { text: "🔥🔥 Superhot", value: "urgent" },
      ],
      render: (priority) => (
        <Tag color={getPriorityColor(priority)}>
          {getPriorityEmoji(priority)} {getPriorityLabel(priority)}
        </Tag>
      ),
    },
    {
      title: "Value",
      dataIndex: "estimatedValue",
      key: "estimatedValue",
      sorter: true,
      render: (value) => (value ? formatCurrency(value) : "-"),
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "source",
      filters: [
        { text: "Website", value: "website" },
        { text: "Referral", value: "referral" },
        { text: "Social Media", value: "social_media" },
        { text: "Email Campaign", value: "email_campaign" },
        { text: "Cold Call", value: "cold_call" },
        { text: "Trade Show", value: "trade_show" },
        { text: "Other", value: "other" },
      ],
      render: (source) =>
        source ? source.replace("_", " ").toUpperCase() : "-",
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: true,
      render: (date) => dayjs(date).format("MMM DD, YYYY"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/leads/${record._id}`);
            }}
            className="focus-visible:focus"
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/leads/${record._id}/edit`);
            }}
            className="focus-visible:focus"
          />
          {user?.role === 'tenant_admin' && (
            <Button
              type="text"
              icon={<DeleteOutlined />}
              danger
              onClick={(e) => {
                e.stopPropagation();
                setLeadToDelete(record);
                setDeleteModalVisible(true);
              }}
              className="focus-visible:focus"
            />
          )}
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <Title level={2} className="mb-2" style={{ display: 'flex', alignItems: 'center' }}>
            <PageIcon name="leads" /> Leads
          </Title>
          <Text className="text-gray-600 dark:text-gray-400">
            Manage your sales leads and prospects
          </Text>
        </div>
        <Space>
          {selectedRowKeys.length > 0 && user?.role === 'tenant_admin' && (
            <Button
              onClick={() => setAssignModalVisible(true)}
              className="focus-visible:focus"
            >
              Assign Selected ({selectedRowKeys.length})
            </Button>
          )}
          <Button
            icon={<ImportOutlined />}
            onClick={() => setBulkUploadModalVisible(true)}
            className="focus-visible:focus"
          >
            Bulk Upload
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate("/leads/new")}
            className="focus-visible:focus"
          >
            Add Lead
          </Button>
        </Space>
      </div>

      {/* Filters */}
      <Card className="shadow-soft">
        <div className="flex flex-wrap gap-4 items-center">
          <Input.Search
            placeholder="Search leads..."
            allowClear
            onSearch={handleSearch}
            value={filters.search || ''}
            onChange={(e) => {
              setFilters({ search: e.target.value });
            }}
            style={{ width: 300 }}
            className="focus-visible:focus"
          />

          <Select
            placeholder="Status"
            allowClear
            style={{ width: 120 }}
            onChange={(value) => handleFilterChange("status", value)}
            value={statusFilterValue}
            className="focus-visible:focus"
          >
            <Option value="new">New</Option>
            <Option value="contacted">Contacted</Option>
            <Option value="converted">Converted</Option>
            <Option value="lost">Lost</Option>
          </Select>

          <Select
            placeholder="Priority"
            allowClear
            style={{ width: 120 }}
            onChange={(value) => handleFilterChange("priority", value)}
            value={priorityFilterValue}
            className="focus-visible:focus"
          >
            <Option value="low">❄️ Cold</Option>
            <Option value="medium">🌤️ Warm</Option>
            <Option value="high">🔥 Hot</Option>
            <Option value="urgent">🔥🔥 Superhot</Option>
          </Select>

          <Select
            placeholder="Source"
            allowClear
            style={{ width: 150 }}
            onChange={(value) => handleFilterChange("source", value)}
            value={sourceFilterValue}
            className="focus-visible:focus"
          >
            <Option value="website">Website</Option>
            <Option value="referral">Referral</Option>
            <Option value="social_media">Social Media</Option>
            <Option value="email_campaign">Email Campaign</Option>
            <Option value="cold_call">Cold Call</Option>
            <Option value="trade_show">Trade Show</Option>
            <Option value="other">Other</Option>
          </Select>

          {user?.role === 'tenant_admin' && (
            <Select
              placeholder="Assigned To"
              allowClear
              style={{ width: 180 }}
              onChange={(value) => handleFilterChange("assignedTo", value)}
              value={assignedToFilterValue}
              loading={loadingTeamMembers}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              className="focus-visible:focus"
            >
              {teamMembers.map((member) => (
                <Option key={member.id} value={member.id}>
                  {member.name}
                </Option>
              ))}
            </Select>
          )}

          <Button
            icon={<FilterOutlined />}
            onClick={handleClearFilters}
            className="focus-visible:focus"
          >
            Clear Filters {countActiveFilters() > 0 && <Badge count={countActiveFilters()} />}
          </Button>
        </div>
        <FilterTags />
      </Card>

      {/* Table */}
      <Card className="shadow-soft">
        <Table
          rowSelection={rowSelection}
          columns={columns}
          dataSource={leads}
          rowKey="_id"
          loading={isLoading}
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
            style: {
              cursor: "pointer",
              backgroundColor: getPriorityRowColor(record.priority),
            },
          })}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} leads`,
          }}
          onChange={handleTableChange}
          className="focus-visible:focus"
        />
      </Card>

      {/* Delete Modal */}
      <Modal
        title="Delete Lead"
        open={deleteModalVisible}
        onOk={() => handleDelete(leadToDelete?._id)}
        onCancel={() => {
          setDeleteModalVisible(false);
          setLeadToDelete(null);
        }}
        okText="Delete"
        okType="danger"
        cancelText="Cancel"
      >
        <p>
          Are you sure you want to delete{" "}
          <strong>
            {leadToDelete?.firstName} {leadToDelete?.lastName}
          </strong>
          ? This action cannot be undone.
        </p>
      </Modal>

      {/* Bulk Upload Modal */}
      <BulkUploadModal
        visible={bulkUploadModalVisible}
        onClose={() => setBulkUploadModalVisible(false)}
        onSuccess={() => {
          fetchLeads({ page: 1, limit: pagination.limit, ...filters });
        }}
      />
      
      {/* Bulk Assignment Modal - Only for tenant admins */}
      {user?.role === 'tenant_admin' && (
        <AssignLeadsModal
          visible={assignModalVisible}
          onClose={() => {
            setAssignModalVisible(false);
            setSelectedRowKeys([]);
          }}
          selectedLeadIds={selectedRowKeys}
          onSuccess={() => {
            fetchLeads({ page: pagination.page, limit: pagination.limit, ...filters });
            setSelectedRowKeys([]);
          }}
        />
      )}
    </div>
  );
};

const LeadForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { createLead, updateLead, fetchLeadById } = useLeadStore();
  const { currencySymbol } = useSettingsStore();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [profileImageList, setProfileImageList] = useState([]);
  const [initialProfileImageUrl, setInitialProfileImageUrl] = useState("");
  const [removeExistingImage, setRemoveExistingImage] = useState(false);
  const leadId = id && id !== "new" ? id : null;
  const { user } = useAuthStore(); // Make sure you have access to the current user

  const normFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList || [];
  };

  const handleProfileImageChange = ({ fileList: newFileList }) => {
    setProfileImageList(newFileList);
    if (newFileList.length === 0) {
      setRemoveExistingImage(Boolean(initialProfileImageUrl));
    } else {
      setRemoveExistingImage(false);
    }
    form.setFieldsValue({ profileImage: newFileList });
  };

  const handleBeforeUpload = (file) => {
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      message.error("Only image files are allowed.");
      return Upload.LIST_IGNORE;
    }
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error("Image must be smaller than 5MB.");
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  useEffect(() => {
    if (leadId) {
      const loadLead = async () => {
        setIsLoading(true);
        const result = await fetchLeadById(leadId);
        if (result.success && result.data) {
          form.setFieldsValue({
            ...result.data,
            expectedCloseDate: result.data.expectedCloseDate
              ? dayjs(result.data.expectedCloseDate)
              : null,
          });

          const existingImageList = result.data.profileImageUrl
            ? [
                {
                  uid: "-1",
                  name: "profile-image",
                  status: "done",
                  url: result.data.profileImageUrl,
                },
              ]
            : [];
          form.setFieldsValue({ profileImage: existingImageList });
          setProfileImageList(existingImageList);
          setInitialProfileImageUrl(result.data.profileImageUrl || "");
          setRemoveExistingImage(false);
        } else {
          form.setFieldsValue({ profileImage: [] });
          setProfileImageList([]);
          setInitialProfileImageUrl("");
          setRemoveExistingImage(false);
        }
        setIsLoading(false);
      };
      loadLead();
    } else {
      form.resetFields();
      form.setFieldsValue({ profileImage: [] });
      setProfileImageList([]);
      setInitialProfileImageUrl("");
      setRemoveExistingImage(false);
    }
  }, [leadId, fetchLeadById, form]);

  const handleSubmit = async (values) => {
    setIsLoading(true);
    try {
      const payload = {
        ...values,
        expectedCloseDate: values.expectedCloseDate?.toISOString(),
      };

      const notesPayload = values.notesText
        ? [
            {
              content: values.notesText,
              createdBy: user?._id,
            },
          ]
        : undefined;

      delete payload.notesText;
      delete payload.profileImage;

      if (notesPayload) {
        payload.notes = notesPayload;
      }

      const formData = new FormData();

      Object.entries(payload).forEach(([key, value]) => {
        if (
          value === undefined ||
          value === null ||
          (typeof value === "string" && value.trim() === "")
        ) {
          return;
        }

        if (Array.isArray(value) || typeof value === "object") {
          formData.append(key, JSON.stringify(value));
        } else {
          formData.append(key, value);
        }
      });

      const newProfileFile = profileImageList.find(
        (file) => file.originFileObj
      );

      if (newProfileFile?.originFileObj) {
        formData.append("profileImage", newProfileFile.originFileObj);
      } else if (!profileImageList.length && removeExistingImage) {
        formData.append("removeProfileImage", "true");
      }

      let result;
      if (leadId) {
        result = await updateLead(leadId, formData);
      } else {
        result = await createLead(formData);
      }

      if (result.success) {
        message.success(`Lead ${leadId ? "updated" : "created"} successfully`);
        navigate("/leads");
      } else {
        message.error(
          result.error || `Failed to ${leadId ? "update" : "create"} lead`
        );
      }
    } catch (error) {
      message.error(`Failed to ${leadId ? "update" : "create"} lead`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full p-6">
      <div className="mb-6">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/leads")}
          className="mb-4"
        >
          Back to Leads
        </Button>
        <Title level={2}>{leadId ? "Edit Lead" : "Create New Lead"}</Title>
      </div>

      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            status: "new",
            priority: "medium",
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="firstName"
                label="First Name"
                rules={[{ required: true, message: "Please enter first name" },
                  {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (/^\s|\s$/.test(value)) {
                    return Promise.reject(new Error("No leading or trailing spaces allowed"));
                  }
                  return Promise.resolve();
                },
              },
                ]}
              >
                <Input placeholder="Enter first name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="lastName"
                label="Last Name"
                rules={[{ required: true, message: "Please enter last name" },
                  {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (/^\s|\s$/.test(value)) {
                    return Promise.reject(new Error("No leading or trailing spaces allowed"));
                  }
                  return Promise.resolve();
                },
              },
                ]}
              >
                <Input placeholder="Enter last name" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="email"
            label="Email"
            validateTrigger="onChange"
            rules={[
              { required: true, message: "Please enter email" },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();

                  // Check for development environment (matches backend logic)
                  const isDevelopment = process.env.NODE_ENV === 'development';

                  let isValid;
                  if (isDevelopment) {
                    // Development: Allow + character for email aliasing
                    isValid = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+[.][a-zA-Z]{2,}$/.test(value);
                  } else {
                    // Production: Block + character and use stricter validation
                    isValid = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*([.]\w{2,3})+$/.test(value);
                  }

                  if (!isValid) {
                    return Promise.reject(new Error('Please enter a valid email address'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder="example@domain.com" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="Phone"
                rules={[
                  { required: true, message: "Please enter your phone number" },
                  {
                    pattern: /^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/,
                    message: "Please enter a valid phone number.",
                  },
                ]}
              >
                <Input maxLength={15} placeholder="Enter phone number" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="company" label="Company">
                <Input placeholder="Enter company name" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="jobTitle" label="Job Title">
            <Input placeholder="Enter job title" />
          </Form.Item>

          <Form.Item
            label="Profile Image"
            name="profileImage"
            valuePropName="fileList"
            getValueFromEvent={normFile}
          >
            <Upload
              accept="image/*"
              listType="picture-card"
              fileList={profileImageList}
              onChange={handleProfileImageChange}
              beforeUpload={handleBeforeUpload}
              maxCount={1}
            >
              {profileImageList.length >= 1 ? null : (
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>Upload</div>
                </div>
              )}
            </Upload>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="status"
                label="Status"
                rules={[{ required: true, message: "Please select status" }]}
              >
                <Select>
                  <Option value="new">New</Option>
                  <Option value="contacted">Contacted</Option>
                  <Option value="converted">Converted</Option>
                  <Option value="lost">Lost</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="priority"
                label="Priority"
                rules={[{ required: true, message: "Please select priority" }]}
              >
                <Select>
                  <Option value="low">❄️ Cold</Option>
                  <Option value="medium">🌤️ Warm</Option>
                  <Option value="high">🔥 Hot</Option>
                  <Option value="urgent">🔥🔥 Superhot</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="source" label="Lead Source">
                <Select allowClear placeholder="Select lead source">
                  <Option value="website">Website</Option>
                  <Option value="referral">Referral</Option>
                  <Option value="social_media">Social Media</Option>
                  <Option value="email_campaign">Email Campaign</Option>
                  <Option value="cold_call">Cold Call</Option>
                  <Option value="trade_show">Trade Show</Option>
                  <Option value="other">Other</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="estimatedValue"
                label={`Estimated Value (${currencySymbol})`}
                tooltip="Captured in INR. Display currency is set in Settings → Regional."
              >
                <InputNumber
                  min={0}
                  formatter={(value) =>
                    `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value.replace(/[^\d]/g, "")}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="expectedCloseDate" label="Expected Close Date">
            <DatePicker className="w-full" />
          </Form.Item>

          <Form.Item name="notesText" label="Notes">
            <TextArea
              rows={4}
              placeholder="Add any additional notes about this lead..."
            />
          </Form.Item>

          <Form.Item className="mb-0 text-right">
            <Space>
              <Button onClick={() => navigate("/leads")}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={isLoading}>
                {leadId ? "Update Lead" : "Create Lead"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default LeadsPage;
